/**
 * Centralised date formatting utilities.
 * All components should use these instead of inline `toLocaleDateString` calls.
 */

const LOCALE = 'en-AU';

/**
 * Parses date strings in YYYY-MM-DD or DD/MM/YYYY formats into a Date object,
 * avoiding timezone-shift issues by treating date-only strings as local time.
 */
function parseDate(date: string | Date): Date {
  if (date instanceof Date) return date;
  // DD/MM/YYYY or DD-MM-YYYY
  const dmyMatch = date.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (dmyMatch) {
    return new Date(Number(dmyMatch[3]), Number(dmyMatch[2]) - 1, Number(dmyMatch[1]));
  }
  // YYYYMMDD (8 digits, no separators — from kyc_data.date_of_birth_yyyymmdd)
  const ymdCompact = date.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (ymdCompact) {
    return new Date(Number(ymdCompact[1]), Number(ymdCompact[2]) - 1, Number(ymdCompact[3]));
  }
  // YYYY-MM-DD date-only (treat as local to avoid UTC midnight offset)
  const isoDateOnly = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoDateOnly) {
    return new Date(Number(isoDateOnly[1]), Number(isoDateOnly[2]) - 1, Number(isoDateOnly[3]));
  }
  // Full ISO datetime string or anything else — let the engine parse it
  return new Date(date);
}

/** "4 Mar 2026" */
export function formatDateShort(date: string | Date | undefined): string {
  if (!date) return '-';
  const d = parseDate(date);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString(LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** "4 March 2026" */
export function formatDateLong(date: string | Date | undefined): string {
  if (!date) return '-';
  const d = parseDate(date);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString(LOCALE, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** "04/03/2026" */
export function formatDateNumeric(date: string | Date | undefined): string {
  if (!date) return '-';
  const d = parseDate(date);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString(LOCALE);
}

/** "4 Mar 2026, 2:30 PM" */
export function formatDateTime(date: string | Date | undefined): string {
  if (!date) return '-';
  const d = parseDate(date);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString(LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/** "2:30 PM" */
export function formatTime(date: string | Date | undefined): string {
  if (!date) return '-';
  const d = parseDate(date);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleTimeString(LOCALE, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/** Relative time: "2 hours ago", "3 days ago", etc. */
export function formatRelativeTime(date: string | Date | undefined): string {
  if (!date) return '-';
  const now = Date.now();
  const then = parseDate(date).getTime();
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
  if (diffDay < 30) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  return formatDateShort(date);
}
